import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt, faUser, faBriefcase, faHome, faAddressBook,
  faFileUpload, faCheckCircle, faArrowLeft, faPlus
} from "@fortawesome/free-solid-svg-icons";
import "./rental-application.css";

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function formatDateOnly(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateOnly(value) {
  if (!DATE_ONLY_REGEX.test(String(value || ""))) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDateOnly(parsed) === value ? parsed : null;
}

function addYears(date, years) {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
}

const DEFAULT_SECTIONS = [
  {
    id: "personal",
    title: "Personal Information",
    icon: faUser,
    questions: [
      { id: "full_name", label: "Full Name", type: "text", required: true, placeholder: "Your full legal name" },
      { id: "dob", label: "Date of Birth", type: "date", required: true },
      { id: "citizenship_no", label: "Citizenship Number / National ID", type: "text", required: true, placeholder: "e.g. 12-01-76-12345" },
      { id: "phone", label: "Phone Number", type: "text", required: true, placeholder: "98XXXXXXXX" },
      { id: "email", label: "Email Address", type: "email", required: true, placeholder: "your@email.com" },
      { id: "current_address", label: "Current Address", type: "textarea", required: true, placeholder: "Ward No., Municipality, District, Province" },
    ],
  },
  {
    id: "employment",
    title: "Employment & Income",
    icon: faBriefcase,
    questions: [
      { id: "occupation", label: "Occupation / Job Title", type: "text", required: true, placeholder: "e.g. Software Engineer, Teacher" },
      { id: "employer", label: "Employer / Company Name", type: "text", required: false, placeholder: "Leave blank if self-employed" },
      { id: "monthly_income", label: "Monthly Income (NPR)", type: "number", required: true, placeholder: "e.g. 50000" },
    ],
  },
  {
    id: "rental",
    title: "Rental Details",
    icon: faHome,
    questions: [
      { id: "move_in_date", label: "Desired Move-in Date", type: "date", required: true },
      {
        id: "lease_duration", label: "Preferred Lease Duration", type: "select", required: true,
        options: ["6 months", "1 year", "2 years", "Other"],
      },
      { id: "num_occupants", label: "Number of Occupants (including yourself)", type: "number", required: true, placeholder: "e.g. 2" },
      { id: "has_pets", label: "Do you have pets?", type: "radio", required: true, options: ["Yes", "No"] },
    ],
  },
  {
    id: "references",
    title: "References",
    icon: faAddressBook,
    questions: [
      { id: "prev_landlord", label: "Previous Landlord Name", type: "text", required: false, placeholder: "Optional" },
      { id: "prev_landlord_phone", label: "Previous Landlord Phone", type: "text", required: false, placeholder: "Optional" },
      { id: "reason_moving", label: "Reason for Moving", type: "textarea", required: false, placeholder: "Brief description" },
    ],
  },
];

const REQUIRED_DOCS = [
  { id: "citizenship_doc", label: "Citizenship Card / National ID Copy", required: true, accept: ".jpg,.jpeg,.png,.pdf" },
  { id: "income_proof", label: "Income Proof (Salary Slip or Bank Statement)", required: true, accept: ".jpg,.jpeg,.png,.pdf" },
  { id: "police_clearance", label: "Police Clearance Certificate", required: true, accept: ".jpg,.jpeg,.png,.pdf" },
];

function RentalApplication() {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState(0);

  const today = new Date();
  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const minDobDate = formatDateOnly(addYears(todayDateOnly, -100));
  const maxDobDate = formatDateOnly(addYears(todayDateOnly, -18));
  const minMoveInDate = formatDateOnly(todayDateOnly);
  const maxMoveInDate = formatDateOnly(addYears(todayDateOnly, 2));

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    if (!token) { navigate("/login"); return; }
    if (role !== "renter") { navigate("/find-property"); return; }

    Promise.all([
      fetch(`/api/properties/${propertyId}`).then((r) => r.json()),
      fetch(`/api/properties/${propertyId}/application-template`).then((r) => r.json()),
    ]).then(([prop, tpl]) => {
      setProperty(prop);
      setCustomQuestions(Array.isArray(tpl.customQuestions) ? tpl.customQuestions : []);

      // Pre-fill name/email/phone from localStorage
      const firstname = localStorage.getItem("firstname") || "";
      const lastname = localStorage.getItem("lastname") || "";
      setAnswers((prev) => ({
        ...prev,
        full_name: `${firstname} ${lastname}`.trim(),
      }));
    }).catch((err) => {
      console.error("Failed to load application form:", err);
    }).finally(() => setLoading(false));
  }, [propertyId, navigate]);

  const handleAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => { const e = { ...prev }; delete e[id]; return e; });
  };

  const handleFile = (fieldId, file) => {
    if (!file) return;

    setPreviewUrls((prev) => {
      const next = { ...prev };
      if (next[fieldId]) URL.revokeObjectURL(next[fieldId]);
      next[fieldId] = URL.createObjectURL(file);
      return next;
    });

    setFiles((prev) => ({ ...prev, [fieldId]: file }));
    setFileNames((prev) => ({ ...prev, [fieldId]: file.name }));
    if (errors[fieldId]) setErrors((prev) => { const e = { ...prev }; delete e[fieldId]; return e; });
  };

  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  const getFilePreviewType = (file) => {
    if (!file) return "none";
    const mime = (file.type || "").toLowerCase();
    const lowerName = (file.name || "").toLowerCase();
    if (mime.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(lowerName)) return "image";
    if (mime === "application/pdf" || lowerName.endsWith(".pdf")) return "pdf";
    return "other";
  };

  const renderFilePreview = (fieldId) => {
    const file = files[fieldId];
    const previewUrl = previewUrls[fieldId];
    if (!file || !previewUrl) return null;

    const type = getFilePreviewType(file);
    if (type === "image") {
      return (
        <div className="doc-preview-box mt-2">
          <img src={previewUrl} alt={file.name} className="doc-preview-image" />
        </div>
      );
    }

    if (type === "pdf") {
      return (
        <div className="doc-preview-box mt-2">
          <iframe
            src={previewUrl}
            title={file.name}
            className="doc-preview-pdf"
          />
        </div>
      );
    }

    return (
      <div className="small text-muted mt-2">Preview not available for this file type.</div>
    );
  };

  const validateQuestion = (q, fieldValue, newErrors) => {
    const value = String(fieldValue || "").trim();

    if (q.required && !value) {
      newErrors[q.id] = `${q.label} is required`;
      return;
    }

    if (!value) return;

    switch (q.id) {
      case "full_name":
        if (value.length < 3) newErrors[q.id] = "Full Name must be at least 3 characters";
        break;
      case "dob": {
        const parsed = parseDateOnly(value);
        if (!parsed) {
          newErrors[q.id] = "Date of Birth must be in YYYY-MM-DD format";
          break;
        }
        if (value < minDobDate || value > maxDobDate) {
          newErrors[q.id] = "You must be at least 18 years old";
        }
        break;
      }
      case "citizenship_no":
        if (value.length < 5) newErrors[q.id] = "Citizenship / National ID is too short";
        break;
      case "phone":
      case "prev_landlord_phone":
        if (!/^\+?[0-9]{10,15}$/.test(value)) {
          newErrors[q.id] = `${q.label} must contain 10-15 digits`;
        }
        break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value)) {
          newErrors[q.id] = "Please enter a valid email address";
        }
        break;
      case "monthly_income": {
        const income = Number(value);
        if (!Number.isFinite(income) || income <= 0) {
          newErrors[q.id] = "Monthly Income must be a positive number";
        }
        break;
      }
      case "move_in_date": {
        const parsed = parseDateOnly(value);
        if (!parsed) {
          newErrors[q.id] = "Desired Move-in Date must be in YYYY-MM-DD format";
          break;
        }
        if (value < minMoveInDate || value > maxMoveInDate) {
          newErrors[q.id] = `Move-in Date must be between ${minMoveInDate} and ${maxMoveInDate}`;
        }
        break;
      }
      case "lease_duration": {
        const allowed = new Set(["6 months", "1 year", "2 years", "Other"]);
        if (!allowed.has(value)) {
          newErrors[q.id] = "Please select a valid lease duration";
        }
        break;
      }
      case "num_occupants": {
        const occupants = Number(value);
        if (!Number.isInteger(occupants) || occupants < 1 || occupants > 20) {
          newErrors[q.id] = "Number of Occupants must be between 1 and 20";
        }
        break;
      }
      case "has_pets":
        if (!["Yes", "No"].includes(value)) {
          newErrors[q.id] = "Please select Yes or No";
        }
        break;
      default:
        break;
    }
  };

  const validate = (sectionIndex = null) => {
    const newErrors = {};

    const includeDefaultSection = (idx) => sectionIndex === null || sectionIndex === idx;

    DEFAULT_SECTIONS.forEach((section, idx) => {
      if (!includeDefaultSection(idx)) return;
      section.questions.forEach((q) => {
        validateQuestion(q, answers[q.id], newErrors);
      });
    });

    const customSectionIndex = DEFAULT_SECTIONS.length;
    if (customQuestions.length > 0 && (sectionIndex === null || sectionIndex === customSectionIndex)) {
      customQuestions.forEach((q, idx) => {
        if (q.required && q.type !== "file" && !answers[`custom_${idx}`]?.toString().trim()) {
          newErrors[`custom_${idx}`] = `${q.label} is required`;
        }
        if (q.required && q.type === "file" && !files[`custom_file_${idx}`]) {
          newErrors[`custom_file_${idx}`] = `${q.label} is required`;
        }
      });
    }

    const docsSectionIndex = DEFAULT_SECTIONS.length + (customQuestions.length > 0 ? 1 : 0);
    if (sectionIndex === null || sectionIndex === docsSectionIndex) {
      REQUIRED_DOCS.forEach((doc) => {
        if (doc.required && !files[doc.id]) {
          newErrors[doc.id] = `${doc.label} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      // Scroll to first error
      const firstErrorEl = document.querySelector(".is-invalid");
      if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem("token");

    try {
      const formData = new FormData();
      formData.append("propertyId", propertyId);
      formData.append("answers", JSON.stringify(answers));

      // Attach document files
      Object.entries(files).forEach(([field, file]) => {
        formData.append(field, file);
      });

      const response = await fetch("/api/applications", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { message: raw || "Unexpected server response" };
      }

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert(data.message || "Failed to submit application");
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("Error connecting to server");
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (q, fieldId, sectionIdx) => {
    const val = answers[fieldId] || "";
    const err = errors[fieldId];
    const inputClass = `form-control${err ? " is-invalid" : ""}`;

    if (q.type === "textarea") {
      return (
        <div key={fieldId} className="mb-3">
          <label className="form-label fw-semibold">
            {q.label} {q.required && <span className="text-danger">*</span>}
          </label>
          <textarea
            className={inputClass}
            rows={3}
            placeholder={q.placeholder || ""}
            value={val}
            onChange={(e) => handleAnswer(fieldId, e.target.value)}
          />
          {err && <div className="invalid-feedback">{err}</div>}
        </div>
      );
    }

    if (q.type === "select") {
      return (
        <div key={fieldId} className="mb-3">
          <label className="form-label fw-semibold">
            {q.label} {q.required && <span className="text-danger">*</span>}
          </label>
          <select
            className={inputClass.replace("form-control", "form-select")}
            value={val}
            onChange={(e) => handleAnswer(fieldId, e.target.value)}
          >
            <option value="">Select an option</option>
            {q.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {err && <div className="invalid-feedback">{err}</div>}
        </div>
      );
    }

    if (q.type === "radio") {
      return (
        <div key={fieldId} className="mb-3">
          <label className="form-label fw-semibold d-block">
            {q.label} {q.required && <span className="text-danger">*</span>}
          </label>
          <div className={`d-flex gap-4${err ? " is-invalid-group" : ""}`}>
            {q.options.map((opt) => (
              <div key={opt} className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name={fieldId}
                  id={`${fieldId}_${opt}`}
                  value={opt}
                  checked={val === opt}
                  onChange={(e) => handleAnswer(fieldId, e.target.value)}
                />
                <label className="form-check-label" htmlFor={`${fieldId}_${opt}`}>{opt}</label>
              </div>
            ))}
          </div>
          {err && <div className="text-danger small mt-1">{err}</div>}
        </div>
      );
    }

    if (q.type === "file") {
      const fileFieldId = `custom_file_${sectionIdx}`;
      const fileErr = errors[fileFieldId];
      return (
        <div key={fieldId} className="mb-3">
          <label className="form-label fw-semibold">
            {q.label} {q.required && <span className="text-danger">*</span>}
          </label>
          <div className={`file-upload-box${fileErr ? " border-danger" : ""}`}>
            <input
              type="file"
              id={fileFieldId}
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: "none" }}
              onChange={(e) => handleFile(fileFieldId, e.target.files[0])}
            />
            <label htmlFor={fileFieldId} className="file-upload-label">
              <FontAwesomeIcon icon={faFileUpload} className="me-2" />
              {fileNames[fileFieldId] || "Choose file (JPG, PNG, PDF — max 5MB)"}
            </label>
          </div>
          {renderFilePreview(fileFieldId)}
          {fileErr && <div className="text-danger small mt-1">{fileErr}</div>}
        </div>
      );
    }

    return (
      <div key={fieldId} className="mb-3">
        <label className="form-label fw-semibold">
          {q.label} {q.required && <span className="text-danger">*</span>}
        </label>
        <input
          type={q.type}
          className={inputClass}
          placeholder={q.placeholder || ""}
          value={val}
          onChange={(e) => handleAnswer(fieldId, e.target.value)}
          min={q.id === "move_in_date" ? minMoveInDate : q.id === "dob" ? minDobDate : undefined}
          max={q.id === "move_in_date" ? maxMoveInDate : q.id === "dob" ? maxDobDate : undefined}
          step={q.type === "number" ? "1" : undefined}
        />
        {err && <div className="invalid-feedback">{err}</div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3 text-muted">Loading application form...</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container py-5 text-center">
        <div className="application-success-card mx-auto">
          <div className="success-icon mb-4">
            <FontAwesomeIcon icon={faCheckCircle} size="4x" className="text-success" />
          </div>
          <h2 className="fw-bold mb-2">Application Submitted!</h2>
          <p className="text-muted mb-4">
            Your rental application for <strong>{property?.name}</strong> has been sent to the owner.
            You'll be notified once it's reviewed. You can track the status in your dashboard.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <button className="btn btn-primary" onClick={() => navigate("/renter-dashboard")}>
              Go to Dashboard
            </button>
            <button className="btn btn-outline-secondary" onClick={() => navigate("/find-property")}>
              Browse More Properties
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalSections = DEFAULT_SECTIONS.length + (customQuestions.length > 0 ? 1 : 0) + 1; // +1 for docs
  const progress = Math.round(((activeSection + 1) / totalSections) * 100);

  return (
    <div className="rental-application-page">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h2 className="mb-0 fw-bold">
              <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
              Rental Application
            </h2>
            {property && (
              <p className="text-muted mb-0 small">
                {property.name} &mdash; NPR {Number(property.price).toLocaleString("en-NP")}/mo
              </p>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>Section {activeSection + 1} of {totalSections}</span>
            <span>{progress}% complete</span>
          </div>
          <div className="progress" style={{ height: "6px" }}>
            <div className="progress-bar bg-primary" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="row">
            {/* Section nav (desktop sidebar) */}
            <div className="col-md-3 d-none d-md-block">
              <div className="section-nav card p-3">
                <p className="fw-semibold text-muted small mb-2 text-uppercase">Sections</p>
                {DEFAULT_SECTIONS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`section-nav-btn${activeSection === i ? " active" : ""}`}
                    onClick={() => {
                      if (i <= activeSection || validate(activeSection)) setActiveSection(i);
                    }}
                  >
                    <FontAwesomeIcon icon={s.icon} className="me-2" />
                    {s.title}
                  </button>
                ))}
                {customQuestions.length > 0 && (
                  <button
                    type="button"
                    className={`section-nav-btn${activeSection === DEFAULT_SECTIONS.length ? " active" : ""}`}
                    onClick={() => {
                      const nextIndex = DEFAULT_SECTIONS.length;
                      if (nextIndex <= activeSection || validate(activeSection)) setActiveSection(nextIndex);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Additional Questions
                  </button>
                )}
                <button
                  type="button"
                  className={`section-nav-btn${activeSection === totalSections - 1 ? " active" : ""}`}
                  onClick={() => {
                    const nextIndex = totalSections - 1;
                    if (nextIndex <= activeSection || validate(activeSection)) setActiveSection(nextIndex);
                  }}
                >
                  <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                  Documents
                </button>
              </div>
            </div>

            {/* Main content */}
            <div className="col-md-9">
              {/* Default sections */}
              {DEFAULT_SECTIONS.map((section, sIdx) => (
                activeSection === sIdx && (
                  <div key={section.id} className="card application-section-card">
                    <div className="card-header section-header">
                      <FontAwesomeIcon icon={section.icon} className="me-2" />
                      {section.title}
                    </div>
                    <div className="card-body">
                      {section.questions.map((q) => renderQuestion(q, q.id, sIdx))}
                    </div>
                  </div>
                )
              ))}

              {/* Custom questions section */}
              {customQuestions.length > 0 && activeSection === DEFAULT_SECTIONS.length && (
                <div className="card application-section-card">
                  <div className="card-header section-header">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Additional Questions from Owner
                  </div>
                  <div className="card-body">
                    {customQuestions.map((q, idx) =>
                      renderQuestion(
                        q,
                        q.type === "file" ? `custom_file_${idx}` : `custom_${idx}`,
                        idx
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Documents section */}
              {activeSection === totalSections - 1 && (
                <div className="card application-section-card">
                  <div className="card-header section-header">
                    <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                    Required Documents
                    <small className="ms-2 fw-normal opacity-75">(JPEG, PNG or PDF — max 5MB each)</small>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info small mb-4">
                      All three documents are required for processing your application.
                    </div>
                    {REQUIRED_DOCS.map((doc) => {
                      const err = errors[doc.id];
                      return (
                        <div key={doc.id} className="mb-4">
                          <label className="form-label fw-semibold">
                            {doc.label} <span className="text-danger">*</span>
                          </label>
                          <div className={`file-upload-box${err ? " border-danger" : fileNames[doc.id] ? " border-success" : ""}`}>
                            <input
                              type="file"
                              id={doc.id}
                              accept={doc.accept}
                              style={{ display: "none" }}
                              onChange={(e) => handleFile(doc.id, e.target.files[0])}
                            />
                            <label htmlFor={doc.id} className="file-upload-label">
                              <FontAwesomeIcon
                                icon={fileNames[doc.id] ? faCheckCircle : faFileUpload}
                                className={`me-2 ${fileNames[doc.id] ? "text-success" : ""}`}
                              />
                              {fileNames[doc.id] || "Choose file"}
                            </label>
                          </div>
                          {renderFilePreview(doc.id)}
                          {err && <div className="text-danger small mt-1">{err}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="d-flex justify-content-between mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={activeSection === 0}
                  onClick={() => setActiveSection((s) => s - 1)}
                >
                  Previous
                </button>

                {activeSection < totalSections - 1 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      if (validate(activeSection)) setActiveSection((s) => s + 1);
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-success px-4"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <><span className="spinner-border spinner-border-sm me-2" />Submitting...</>
                    ) : (
                      <><FontAwesomeIcon icon={faCheckCircle} className="me-2" />Submit Application</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RentalApplication;
